import Slide from '@/components/shared/Slide';
import GoNoGoBadge from '@/components/shared/GoNoGoBadge';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { getProgramRealisticFunding } from '@/lib/realistic-funding';
import type { GrantProgram, PriorityProgramOverride, ViewMode } from '@/types/roadmap.types';

interface Props {
  programs: GrantProgram[];
  overrides?: PriorityProgramOverride[];
  mode: ViewMode;
}

/**
 * Return all PRIMARY + GO programs, sorted by composite desc within each tier.
 */
function selectPriority(programs: GrantProgram[]): GrantProgram[] {
  const primary = programs
    .filter((p) => p.go_no_go === 'primary')
    .sort((a, b) => (b.score?.composite ?? 0) - (a.score?.composite ?? 0));

  const go = programs
    .filter((p) => p.go_no_go === 'go')
    .sort((a, b) => (b.score?.composite ?? 0) - (a.score?.composite ?? 0));

  return [...primary, ...go];
}

function ProgramRow({ p, overrides }: { p: GrantProgram; overrides?: PriorityProgramOverride[] }) {
  return (
    <TableRow>
      <TableCell className="font-medium w-[52%] print:text-[7pt] print:py-1.5">
        <div className="leading-snug">{p.program_name}</div>
        <div className="text-[10px] text-muted-foreground font-normal leading-snug print:text-[6pt]">
          {p.grantor}
        </div>
      </TableCell>
      <TableCell className="text-xs w-[27%] print:text-[7pt] print:py-1.5">
        {getProgramRealisticFunding(p, overrides)}
      </TableCell>
      <TableCell className="text-[11px] text-muted-foreground w-[8%] whitespace-nowrap print:text-[7pt] print:py-1.5">
        {overrides?.find((o) => o.program_id === p.id)?.funding_intensity ??
          (p.funding_percentage != null
            ? `${Math.round(p.funding_percentage * 100)}%`
            : (p.funding_level_display ?? '—'))}
      </TableCell>
      <TableCell className="w-[13%] px-2 text-center whitespace-nowrap print:px-1">
        <GoNoGoBadge value={p.go_no_go} />
      </TableCell>
    </TableRow>
  );
}

export default function PriorityProgramsSlide({ programs, overrides, mode }: Props) {
  const priority = selectPriority(programs);

  return (
    <Slide mode={mode} wide>
      <div className="w-full max-w-[1400px] mx-auto">
        <div className="px-4">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
            Executive Decision Summary
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-6 print:mb-3 print:text-[16pt]">
            Top Priority Programs
          </h2>
        </div>

        <Table className="table-fixed">
          <TableHeader>
            <TableRow className="border-b-2 border-primary/10 hover:bg-transparent">
              <TableHead className="w-[52%] print:text-[7pt]">Program</TableHead>
              <TableHead className="w-[27%] print:text-[7pt]">Realistic Funding</TableHead>
              <TableHead className="w-[8%] print:text-[7pt]">Intensity</TableHead>
              <TableHead className="w-[13%] px-2 text-center print:text-[7pt] print:px-1">
                Status
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {priority.map((p) => (
              <ProgramRow key={p.id} p={p} overrides={overrides} />
            ))}
          </TableBody>
        </Table>

        <p className="mt-4 px-4 text-[11px] text-muted-foreground print:text-[7pt] print:mt-3">
          &ldquo;Realistic Funding&rdquo; reflects the estimated client share, not total program envelopes.
          See individual program pages for full details.
        </p>
      </div>
    </Slide>
  );
}
